/**
 * Desktop Sidebar — Extracted from DashboardMainResponsive (SRP)
 * Accordion-based sidebar navigation for desktop layout
 */

import React, { useState } from 'react';
import {
  Activity,
  Archive,
  Banknote,
  BarChart2,
  Bell,
  Book,
  Building2,
  Calculator,
  Calendar,
  ChevronRight,
  ClipboardList,
  Clock,
  Euro,
  FileSignature,
  FileSpreadsheet,
  FileText,
  FileX,
  Files,
  FolderKanban,
  FolderOpen,
  Gauge,
  HardHat,
  HelpCircle,
  Headphones,
  Key,
  Landmark,
  LayoutDashboard,
  Link as LinkIcon,
  Link2,
  Mail,
  Network,
  Package,
  Palette,
  Percent,
  PieChart,
  Plug,
  Receipt,
  RefreshCw,
  Repeat,
  Scale,
  Send,
  Settings,
  Shield,
  ShoppingCart,
  Smartphone,
  Sparkles,
  Sun,
  TrendingUp,
  Upload,
  UserCheck,
  Users,
  Webhook,
  Wrench,
  Zap,
} from 'lucide-react';
import { AccordionMenuItem, SubMenuItem } from '@/components/ui-v2/AccordionMenuItem';
import type { Tab } from './DashboardMainResponsive';
import type { SectionId } from '@/config/uiModeConfig';

/**
 * Collapsible toggle for Référentiels (Plan comptable, Taux TVA, Exercices fiscaux)
 * These are configuration items modified 1-2 times/year — hidden by default (Progressive Disclosure)
 */
const ReferencesCollapsible: React.FC<{
  activeTab: Tab;
  onTabChange: (tab: Tab) => void;
  t: (key: string) => string;
}> = ({ activeTab, onTabChange, t }) => {
  const [isOpen, setIsOpen] = useState(
    activeTab === 'chart-of-accounts' || activeTab === 'tax-rates' || activeTab === 'fiscal-years'
  );

  return (
    <>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center gap-2 px-3 pt-3 pb-1 group cursor-pointer"
      >
        <ChevronRight
          className={`w-3 h-3 text-gray-400 group-hover:text-teal-600 transition-transform duration-200 ${isOpen ? 'rotate-90' : ''}`}
        />
        <span className="text-[10px] font-semibold text-gray-400 group-hover:text-gray-600 uppercase tracking-wider transition-colors">
          {t('nav.subsections.references')}
        </span>
      </button>
      {isOpen && (
        <div className="space-y-1">
          <SubMenuItem icon={FileSpreadsheet} label={t('nav.items.chartOfAccounts')} isActive={activeTab === 'chart-of-accounts'} onClick={() => onTabChange('chart-of-accounts')} />
          <SubMenuItem icon={Receipt} label={t('nav.items.taxRates')} isActive={activeTab === 'tax-rates'} onClick={() => onTabChange('tax-rates')} />
          <SubMenuItem icon={Calendar} label={t('nav.items.fiscalYears')} isActive={activeTab === 'fiscal-years'} onClick={() => onTabChange('fiscal-years')} />
        </div>
      )}
    </>
  );
};

interface BuildSidebarParams {
  activeTab: Tab;
  onTabChange: (tab: Tab) => void;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  expandedMenu: string | null;
  setExpandedMenu: (menu: string | null) => void;
  badges: any;
  sidebarCollapsed: boolean;
  navigate: (path: string) => void;
  shouldShowSection: (sectionId: SectionId) => boolean;
  t: (key: string) => string;
  pathname: string;
}

export const buildOptimizedSidebar = ({
  activeTab,
  onTabChange,
  isAdmin,
  isSuperAdmin,
  expandedMenu,
  setExpandedMenu,
  badges,
  sidebarCollapsed,
  navigate,
  shouldShowSection,
  t,
  pathname,
}: BuildSidebarParams): React.ReactNode => {
  const handleMenuToggle = (menuId: string) => {
    setExpandedMenu(expandedMenu === menuId ? null : menuId);
  };

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* Menu Items */}
      <div className="flex-1 overflow-y-auto space-y-1 p-4 pb-8">
      {/* 1. Tableau de bord */}
      <AccordionMenuItem
        collapsed={sidebarCollapsed}
        icon={BarChart2}
        label={t('nav.items.dashboard')}
        badge={badges.unpaidInvoices + badges.overdueInvoices}
        badgeVariant="alert"
        isActive={activeTab === 'dashboard'}
        onClick={() => onTabChange('dashboard')}
      />

      {/* CRM - Vente */}
      {shouldShowSection('crm') && (
        <AccordionMenuItem collapsed={sidebarCollapsed}
          icon={TrendingUp}
          label={t('nav.items.crmVente')}
          hasChildren
          isExpanded={expandedMenu === 'crm-vente'}
          onToggle={() => handleMenuToggle('crm-vente')}
          onClick={() => onTabChange('crm-vente')}
          isActive={activeTab === 'crm-vente' || activeTab === 'leads' || activeTab === 'opportunities' || activeTab === 'sales-tasks' || activeTab === 'automation-rules'}
        >
          <div className="space-y-1">
            <SubMenuItem icon={BarChart2} label={t('nav.items.kpisConversion')} isActive={activeTab === 'crm-vente'} onClick={() => onTabChange('crm-vente')} />
            <SubMenuItem icon={UserCheck} label={t('nav.items.prospection')} isActive={activeTab === 'leads'} onClick={() => onTabChange('leads')} />
            <SubMenuItem icon={FolderKanban} label={t('nav.items.opportunities')} isActive={activeTab === 'opportunities'} onClick={() => onTabChange('opportunities')} />
            <SubMenuItem icon={Zap} label={t('nav.items.dailyActions')} isActive={activeTab === 'sales-tasks'} onClick={() => onTabChange('sales-tasks')} />
            <SubMenuItem icon={Activity} label={t('nav.items.automationRules')} isActive={activeTab === 'automation-rules'} onClick={() => onTabChange('automation-rules')} />
          </div>
        </AccordionMenuItem>
      )}

      {/* Facturation */}
      <AccordionMenuItem collapsed={sidebarCollapsed}
        icon={Receipt}
        label={t('nav.sections.billing')}
        hasChildren
        isExpanded={expandedMenu === 'billing'}
        onToggle={() => handleMenuToggle('billing')}
        onClick={() => onTabChange('invoices')}
        isActive={activeTab === 'invoices' || activeTab === 'quotes' || activeTab === 'recurring-invoices' || activeTab === 'credit-notes' || activeTab === 'public-links' || activeTab === 'reminders' || activeTab === 'payments/migration'}
      >
        <div className="space-y-1">
          <SubMenuItem icon={Receipt} label={t('nav.items.invoices')} badge={badges.unpaidInvoices} badgeVariant="alert" isActive={activeTab === 'invoices'} onClick={() => onTabChange('invoices')} />
          <SubMenuItem icon={FileText} label={t('nav.items.quotes')} badge={badges.draftQuotes} badgeVariant="warning" isActive={activeTab === 'quotes'} onClick={() => onTabChange('quotes')} />
          <SubMenuItem icon={Repeat} label={t('nav.items.recurring')} isActive={activeTab === 'recurring-invoices'} onClick={() => onTabChange('recurring-invoices')} />
          <SubMenuItem icon={FileX} label={t('nav.items.creditNotes')} isActive={activeTab === 'credit-notes'} onClick={() => onTabChange('credit-notes')} />
          <SubMenuItem icon={LinkIcon} label={t('nav.items.publicLinks')} isActive={activeTab === 'public-links'} onClick={() => onTabChange('public-links')} />
          <div className="px-3 pt-2 pb-1">
            <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">{t('nav.sections.collections')}</span>
          </div>
          <SubMenuItem icon={Bell} label={t('nav.items.remindersClients')} badge={badges.pendingReminders} badgeVariant="warning" isActive={activeTab === 'reminders'} onClick={() => onTabChange('reminders')} />
          <SubMenuItem icon={RefreshCw} label={t('nav.items.sepaMandate')} isActive={activeTab === 'payments/migration'} onClick={() => onTabChange('payments/migration')} />
        </div>
      </AccordionMenuItem>

      {/* Catalogue */}
      <AccordionMenuItem collapsed={sidebarCollapsed}
        icon={Users}
        label={t('nav.sections.catalog')}
        hasChildren
        isExpanded={expandedMenu === 'catalog'}
        onToggle={() => handleMenuToggle('catalog')}
        onClick={() => onTabChange('clients')}
        isActive={activeTab === 'clients' || activeTab === 'suppliers' || activeTab === 'products'}
      >
        <div className="space-y-1">
          <SubMenuItem icon={UserCheck} label={t('nav.items.clients')} isActive={activeTab === 'clients'} onClick={() => onTabChange('clients')} />
          <SubMenuItem icon={Building2} label={t('nav.items.suppliers')} isActive={activeTab === 'suppliers'} onClick={() => onTabChange('suppliers')} />
          <SubMenuItem icon={Package} label={t('nav.items.productsServices')} isActive={activeTab === 'products'} onClick={() => onTabChange('products')} />
        </div>
      </AccordionMenuItem>

      {/* Achats */}
      {shouldShowSection('purchases') && (
        <AccordionMenuItem collapsed={sidebarCollapsed}
          icon={ShoppingCart}
          label={t('nav.items.purchases')}
          hasChildren={false}
          onClick={() => onTabChange('achats')}
          isActive={activeTab === 'achats' || activeTab === 'purchases' || activeTab === 'expenses'}
        />
      )}

      {/* Trésorerie */}
      {shouldShowSection('treasury') && (
        <AccordionMenuItem collapsed={sidebarCollapsed}
          icon={Euro}
          label={t('nav.items.treasury')}
          hasChildren
          isExpanded={expandedMenu === 'treasury'}
          onToggle={() => handleMenuToggle('treasury')}
          onClick={() => onTabChange('treasury')}
          isActive={activeTab === 'treasury' || activeTab === 'treasury/budgets' || activeTab === 'bank-reconciliation'}
        >
          <div className="space-y-1">
            <div className="px-3 pt-2 pb-1">
              <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">{t('nav.sections.treasuryDaily')}</span>
            </div>
            <SubMenuItem icon={Landmark} label={t('nav.items.bankBalances')} isActive={activeTab === 'treasury'} onClick={() => onTabChange('treasury')} />
            <div className="px-3 pt-3 pb-1">
              <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">{t('nav.sections.treasuryForecast')}</span>
            </div>
            <SubMenuItem icon={BarChart2} label={t('nav.items.budgetVsReal')} isActive={activeTab === 'treasury/budgets'} onClick={() => onTabChange('treasury/budgets')} />
            <div className="px-3 pt-3 pb-1">
              <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">{t('nav.sections.reconciliation')}</span>
            </div>
            <SubMenuItem icon={FileSpreadsheet} label={t('nav.items.bankReconciliation')} isActive={activeTab === 'bank-reconciliation'} onClick={() => onTabChange('bank-reconciliation')} />
          </div>
        </AccordionMenuItem>
      )}

      {/* Comptabilité — Restructuré : 14 items → 7 + toggle Référentiels */}
      {shouldShowSection('accounting') && (
        <AccordionMenuItem collapsed={sidebarCollapsed}
          icon={Calculator}
          label={t('nav.items.accounting')}
          hasChildren
          isExpanded={expandedMenu === 'accounting'}
          onToggle={() => handleMenuToggle('accounting')}
          onClick={() => onTabChange('accounting')}
          isActive={
            activeTab === 'accounting' ||
            activeTab === 'journal-entries' || activeTab === 'account-reconciliation' ||
            activeTab === 'general-balance' || activeTab === 'general-ledger' || activeTab === 'financial-statements' ||
            activeTab === 'tva' || activeTab === 'accounting-exports' || activeTab === 'fiscal-year-closure' ||
            activeTab === 'chart-of-accounts' || activeTab === 'tax-rates' || activeTab === 'fiscal-years' ||
            activeTab === 'chorus-pro' || activeTab === 'probatory-archive' ||
            activeTab === 'accounting-entries' || activeTab === 'accounting-balance' || activeTab === 'accounting-declarations'
          }
        >
          <div className="space-y-1">
            <SubMenuItem icon={LayoutDashboard} label={t('nav.items.myResult')} isActive={activeTab === 'accounting'} onClick={() => onTabChange('accounting')} />

            <div className="px-3 pt-2 pb-1"><span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">{t('nav.subsections.operations')}</span></div>
            <SubMenuItem icon={Book} label={t('nav.items.entryAndReconciliation')} isActive={activeTab === 'journal-entries' || activeTab === 'account-reconciliation' || activeTab === 'accounting-entries'} onClick={() => onTabChange('accounting-entries')} />
            <SubMenuItem icon={Scale} label={t('nav.items.balanceAndLedger')} isActive={activeTab === 'general-balance' || activeTab === 'general-ledger' || activeTab === 'accounting-balance'} onClick={() => onTabChange('accounting-balance')} />
            <SubMenuItem icon={BarChart2} label={t('nav.items.financialStatements')} isActive={activeTab === 'financial-statements'} onClick={() => onTabChange('financial-statements')} />

            <div className="px-3 pt-3 pb-1"><span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">{t('nav.subsections.declarations')}</span></div>
            <SubMenuItem icon={Percent} label={t('nav.items.vatAndCompliance')} isActive={activeTab === 'tva' || activeTab === 'chorus-pro' || activeTab === 'accounting-exports' || activeTab === 'probatory-archive' || activeTab === 'accounting-declarations'} onClick={() => onTabChange('accounting-declarations')} />
            <SubMenuItem icon={Archive} label={t('nav.items.fiscalYearClosure')} isActive={activeTab === 'fiscal-year-closure'} onClick={() => onTabChange('fiscal-year-closure')} />

            <ReferencesCollapsible activeTab={activeTab} onTabChange={onTabChange} t={t} />
          </div>
        </AccordionMenuItem>
      )}

      {/* ERP BTP - Gestion Chantiers */}
      {shouldShowSection('erp') && (
        <AccordionMenuItem collapsed={sidebarCollapsed}
          icon={HardHat}
          label={t('nav.items.erp')}
          hasChildren
          isExpanded={expandedMenu === 'erp-chantier'}
          onToggle={() => handleMenuToggle('erp-chantier')}
          onClick={() => onTabChange('erp-cockpit')}
          isActive={pathname.startsWith('/erp-') || pathname.startsWith('/projects')}
        >
          <div className="space-y-1">
            <SubMenuItem icon={BarChart2} label={t('nav.items.cockpit')} isActive={pathname === '/erp-cockpit'} onClick={() => onTabChange('erp-cockpit')} />
            <SubMenuItem icon={Sun} label={t('nav.items.myDay')} isActive={pathname === '/erp-ma-journee'} onClick={() => onTabChange('erp-ma-journee')} />
            <SubMenuItem icon={FolderOpen} label={t('nav.items.projects')} isActive={pathname === '/erp-projects' || pathname.startsWith('/projects')} onClick={() => onTabChange('erp-projects')} />
            <SubMenuItem icon={PieChart} label={t('nav.items.portfolio')} isActive={pathname === '/erp-portfolio' || pathname === '/projects/portfolio'} onClick={() => onTabChange('erp-portfolio')} />
            <SubMenuItem icon={Euro} label={t('nav.items.invoiceAction')} isActive={pathname === '/erp-facturer'} onClick={() => onTabChange('erp-facturer')} />
            <SubMenuItem icon={FileText} label={t('nav.items.situations')} isActive={pathname === '/erp-situations'} onClick={() => onTabChange('erp-situations')} />
            <SubMenuItem icon={Users} label={t('nav.items.teams')} isActive={pathname === '/erp-teams'} onClick={() => onTabChange('erp-teams')} />
            <SubMenuItem icon={Calendar} label={t('nav.items.planning')} isActive={pathname === '/erp-planning'} onClick={() => onTabChange('erp-planning')} />
          </div>
        </AccordionMenuItem>
      )}

      {/* Interventions */}
      {shouldShowSection('interventions') && (
        <AccordionMenuItem collapsed={sidebarCollapsed}
          icon={Wrench}
          label={t('nav.items.interventions')}
          hasChildren
          isExpanded={expandedMenu === 'interventions'}
          onToggle={() => handleMenuToggle('interventions')}
          onClick={() => onTabChange('interventions')}
          isActive={activeTab === 'interventions' || activeTab === 'work-orders' || activeTab === 'planning' || activeTab === 'timesheets' || activeTab === 'field-mode'}
        >
          <div className="space-y-1">
            <div className="px-3 pt-2 pb-1"><span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">{t('nav.subsections.plan')}</span></div>
            <SubMenuItem icon={Calendar} label={t('nav.items.weekPlanning')} isActive={activeTab === 'planning'} onClick={() => onTabChange('planning')} />
            <SubMenuItem icon={ClipboardList} label={t('nav.items.assignments')} isActive={activeTab === 'work-orders'} onClick={() => onTabChange('work-orders')} />
            <div className="px-3 pt-3 pb-1"><span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">{t('nav.subsections.execute')}</span></div>
            <SubMenuItem icon={Smartphone} label={t('nav.items.fieldMode')} isActive={activeTab === 'field-mode'} onClick={() => onTabChange('field-mode')} />
            <SubMenuItem icon={Clock} label={t('nav.items.timeTracking')} isActive={activeTab === 'timesheets'} onClick={() => onTabChange('timesheets')} />
            <div className="px-3 pt-3 pb-1"><span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">{t('nav.subsections.valorize')}</span></div>
            <SubMenuItem icon={BarChart2} label={t('nav.items.kpisProductivity')} isActive={activeTab === 'interventions'} onClick={() => onTabChange('interventions')} />
          </div>
        </AccordionMenuItem>
      )}

      {/* Outils (regroupe Imports, IA, Benchmarks, Financement) */}
      <AccordionMenuItem collapsed={sidebarCollapsed}
        icon={Gauge}
        label={t('nav.sections.tools')}
        hasChildren
        isExpanded={expandedMenu === 'tools'}
        onToggle={() => handleMenuToggle('tools')}
        onClick={() => onTabChange('imports')}
        isActive={activeTab === 'imports' || activeTab === 'imports/history' || activeTab === 'ai-actions' || activeTab === 'benchmarks' || activeTab === 'financing'}
      >
        <div className="space-y-1">
          <SubMenuItem icon={Upload} label={t('nav.items.importStatements')} isActive={activeTab === 'imports' || activeTab === 'imports/history'} onClick={() => onTabChange('imports')} />
          {shouldShowSection('ai') && (
            <SubMenuItem icon={Sparkles} label={t('nav.items.ia')} isActive={activeTab === 'ai-actions'} onClick={() => onTabChange('ai-actions')} />
          )}
          {shouldShowSection('benchmarks') && (
            <SubMenuItem icon={Network} label={t('nav.items.benchmarks')} isActive={activeTab === 'benchmarks'} onClick={() => onTabChange('benchmarks')} />
          )}
          {shouldShowSection('financing') && (
            <SubMenuItem icon={Banknote} label={t('nav.items.financing')} isActive={activeTab === 'financing'} onClick={() => onTabChange('financing')} />
          )}
        </div>
      </AccordionMenuItem>

      {/* Administration unifiee (Admin only) — regroupe Equipe + Documents + Integrations + Monitoring */}
      {isAdmin && (
        <AccordionMenuItem collapsed={sidebarCollapsed}
          icon={Shield}
          label={t('nav.sections.admin')}
          hasChildren
          isExpanded={expandedMenu === 'admin'}
          onToggle={() => handleMenuToggle('admin')}
          onClick={() => onTabChange('users')}
          isActive={
            activeTab === 'users' || activeTab === 'companies' || activeTab === 'user-provisioning' ||
            activeTab === 'templates' || activeTab === 'email-templates' || activeTab === 'signatures' || activeTab === 'legal-mentions' ||
            activeTab === 'accounting-connectors' || activeTab === 'api-keys' || activeTab === 'webhooks' ||
            activeTab === 'reminder-policies' || activeTab === 'email-logs' || activeTab === 'audit-logs' || activeTab === 'system-overview'
          }
        >
          <div className="space-y-1">
            <div className="px-3 pt-2 pb-1"><span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">{t('nav.sections.team')}</span></div>
            <SubMenuItem icon={Users} label={t('nav.items.users')} isActive={activeTab === 'users'} onClick={() => onTabChange('users')} />
            <SubMenuItem icon={Building2} label={t('nav.items.companiesSocietes')} isActive={activeTab === 'companies'} onClick={() => onTabChange('companies')} />
            <SubMenuItem icon={UserCheck} label={t('nav.items.provisioningSSO')} isActive={activeTab === 'user-provisioning'} onClick={() => onTabChange('user-provisioning')} />
            <div className="px-3 pt-3 pb-1"><span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">{t('nav.sections.documents')}</span></div>
            <SubMenuItem icon={Palette} label={t('nav.items.templatesDocs')} isActive={activeTab === 'templates'} onClick={() => onTabChange('templates')} />
            <SubMenuItem icon={Mail} label={t('nav.items.templatesEmail')} isActive={activeTab === 'email-templates'} onClick={() => onTabChange('email-templates')} />
            <SubMenuItem icon={FileSignature} label={t('nav.items.signatures')} isActive={activeTab === 'signatures'} onClick={() => onTabChange('signatures')} />
            <SubMenuItem icon={FileText} label={t('nav.items.legalMentions')} isActive={activeTab === 'legal-mentions'} onClick={() => onTabChange('legal-mentions')} />
            <div className="px-3 pt-3 pb-1"><span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">{t('nav.sections.integrations')}</span></div>
            <SubMenuItem icon={LinkIcon} label={t('nav.items.connectorsAccounting')} isActive={activeTab === 'accounting-connectors'} onClick={() => onTabChange('accounting-connectors')} />
            <SubMenuItem icon={Key} label={t('nav.items.apiKeys')} isActive={activeTab === 'api-keys'} onClick={() => onTabChange('api-keys')} />
            <SubMenuItem icon={Webhook} label={t('nav.items.webhooks')} isActive={activeTab === 'webhooks'} onClick={() => onTabChange('webhooks')} />
            <div className="px-3 pt-3 pb-1"><span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">{t('nav.sections.monitoring')}</span></div>
            <SubMenuItem icon={Bell} label={t('nav.items.configReminders')} isActive={activeTab === 'reminder-policies'} onClick={() => onTabChange('reminder-policies')} />
            <SubMenuItem icon={Mail} label={t('nav.items.emailLogs')} isActive={activeTab === 'email-logs'} onClick={() => onTabChange('email-logs')} />
            <SubMenuItem icon={Book} label={t('nav.items.auditLog')} isActive={activeTab === 'audit-logs'} onClick={() => onTabChange('audit-logs')} />
            <SubMenuItem icon={Gauge} label={t('nav.items.systemView')} isActive={activeTab === 'system-overview'} onClick={() => onTabChange('system-overview')} />
          </div>
        </AccordionMenuItem>
      )}

      {/* Paramètres */}
      <AccordionMenuItem collapsed={sidebarCollapsed}
        icon={Settings}
        label={t('nav.items.settings')}
        isActive={activeTab === 'settings' || activeTab === 'settings/company' || activeTab === 'settings/legal' || activeTab === 'settings/payments' || activeTab === 'settings/integrations' || activeTab === 'settings/appearance' || activeTab === 'settings/security'}
        onClick={() => onTabChange('settings')}
      />

      {/* Aide */}
      <AccordionMenuItem collapsed={sidebarCollapsed} icon={HelpCircle} label={t('nav.items.help')} hasChildren isExpanded={expandedMenu === 'help'} onToggle={() => handleMenuToggle('help')} onClick={() => onTabChange('support')} isActive={activeTab === 'support' || activeTab === 'help'}>
        <div className="space-y-1">
          <SubMenuItem icon={Headphones} label={t('nav.items.support')} isActive={activeTab === 'support'} onClick={() => onTabChange('support')} />
          <SubMenuItem icon={HelpCircle} label={t('nav.items.helpCenter')} isActive={activeTab === 'help'} onClick={() => onTabChange('help')} />
        </div>
      </AccordionMenuItem>

      {/* SuperAdmin Panel */}
      {isSuperAdmin && (
        <AccordionMenuItem collapsed={sidebarCollapsed}
          icon={Shield}
          label={t('nav.items.superadminPanel')}
          onClick={() => navigate('/superadmin/dashboard')}
          className="bg-gradient-to-r from-purple-500/10 to-indigo-500/10 border-l-2 border-purple-500"
        />
      )}
      </div>
    </div>
  );
};
